'use client';

import { useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';
import { toast } from 'sonner';
import { ApiError, categoryBudgetApi } from '@/lib/api';
import { track } from '@/lib/telemetry';
import { notifyError } from '@/lib/notify-error';
import type { CreateCustomCategoryRequest, CustomCategorySummary } from '@/types';
import { useAuthedFetcher } from './swrConfig';

export interface UseCreateCustomCategoryResult {
  /** POSTs, toasts, and updates every cache that lists custom categories. */
  createCategory: (data: CreateCustomCategoryRequest) => Promise<CustomCategorySummary>;
  creating: boolean;
}

/**
 * Owns the full "create a custom category" side-effect chain (POST + toast + cache
 * updates) in one place, so every category picker/creation surface shares it instead
 * of hand-rolling its own refresh logic.
 */
export function useCreateCustomCategory(): UseCreateCustomCategoryResult {
  const authedFetch = useAuthedFetcher();
  const { mutate } = useSWRConfig();
  const [creating, setCreating] = useState(false);

  const createCategory = useCallback(
    async (data: CreateCustomCategoryRequest) => {
      setCreating(true);
      try {
        const { id } = await authedFetch((token) => categoryBudgetApi.createCategory(token, data));
        const created: CustomCategorySummary = {
          id,
          name: data.name,
          icon: data.icon ?? null,
          color: data.color ?? null,
          kind: data.kind,
        };

        // Optimistic append for instant feedback, THEN revalidate: if the
        // ['custom-categories'] cache hasn't finished its first load yet, `current` is
        // undefined here and this temporarily writes a list containing only `created` —
        // revalidate: false would leave that incomplete list stuck (SWR has nothing else
        // scheduled to correct it) until an unrelated trigger like window focus. Filtering
        // out `created.id` before appending also guards the (narrower) case where a racing
        // fetch already resolved with a list that includes it, so this doesn't duplicate it.
        void mutate<CustomCategorySummary[]>(
          ['custom-categories'],
          (current) => [...(current ?? []).filter((c) => c.id !== created.id), created],
          { revalidate: true },
        );
        // Overview rows carry period spend/limit fields this call can't fabricate —
        // refetch (not patch) every scope.
        void mutate((key) => Array.isArray(key) && key[0] === 'category-overview');

        // `via_suggestion` isn't knowable here (the AI-assist runs in the picker
        // UI) — the picker surfaces can extend this later if the split matters.
        track('action.category.create', { via_suggestion: false, kind: data.kind });
        toast.success(`Categoria "${data.name}" criada.`);
        return created;
      } catch (err) {
        if (!(err instanceof ApiError && err.isLimitExceeded())) {
          notifyError('custom_category_create', err, 'Não foi possível criar a categoria.');
        }
        throw err;
      } finally {
        setCreating(false);
      }
    },
    [authedFetch, mutate],
  );

  return { createCategory, creating };
}
